import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../AuthContext';
import { testLoaderService } from '../services/testLoaderService';
import { progressStore } from '../services/progressStore';
import { scoringEngine } from '../services/scoringEngine';
import { TestData, TestAttempt, Question, ChoiceId } from '../types/testy';
import { Spinner } from './LoadingComponents';
import NavigationGrid from './NavigationGrid';
import './TestAccessibility.css';

interface TestRunnerProps {
  testId: string;
  onNavigate: (section: string, testId?: string) => void;
}

interface QuestionStatus {
  answered: boolean;
  flagged: boolean;
}

const TestRunner: React.FC<TestRunnerProps> = ({ testId, onNavigate }) => {
  const { currentUser } = useAuth();
  const [testData, setTestData] = useState<TestData | null>(null);
  const [currentAttempt, setCurrentAttempt] = useState<TestAttempt | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [allQuestions, setAllQuestions] = useState<Question[]>([]);
  const [questionStatuses, setQuestionStatuses] = useState<Record<string, QuestionStatus>>({});
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showNavigationGrid, setShowNavigationGrid] = useState(false);
  const [showSubmitConfirmation, setShowSubmitConfirmation] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showKeyboardHelp, setShowKeyboardHelp] = useState(false);
  
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Flatten all questions from all sections and parts
  const flattenQuestions = useCallback((testData: TestData): Question[] => {
    const questions: Question[] = [];
    testData.sections.forEach(section => {
      section.parts.forEach(part => {
        questions.push(...part.questions);
      });
    });
    return questions;
  }, []);

  // Initialize question statuses
  const initializeQuestionStatuses = useCallback((questions: Question[], attempt?: TestAttempt): Record<string, QuestionStatus> => {
    const statuses: Record<string, QuestionStatus> = {};
    questions.forEach(question => {
      statuses[question.id] = {
        answered: attempt?.answers[question.id] ? true : false,
        flagged: attempt?.flagged.has(question.id) || false
      };
    });
    return statuses;
  }, []);

  // Load test data and existing attempt
  useEffect(() => {
    const loadTestData = async () => {
      if (!currentUser) return;

      try {
        setLoading(true);
        setError(null);

        // Load test data
        const data = await testLoaderService.loadTest(testId);
        setTestData(data);

        const questions = flattenQuestions(data);
        setAllQuestions(questions);

        // Load or create attempt
        let attempt = await progressStore.loadProgress(testId, currentUser.uid);
        
        if (!attempt) {
          // Create new attempt
          const newAttempt = {
            testId,
            userId: currentUser.uid,
            startTime: new Date(),
            timeRemaining: data.durationSec,
            answers: {},
            flagged: new Set<string>(),
            isCompleted: false
          };
          attempt = await progressStore.saveProgress(newAttempt);
        }

        setCurrentAttempt(attempt);
        setTimeRemaining(attempt.timeRemaining);
        setQuestionStatuses(initializeQuestionStatuses(questions, attempt));

        // Find current question index if resuming
        if (attempt && Object.keys(attempt.answers).length > 0) {
          const lastAnsweredIndex = questions.findIndex(q => attempt!.answers[q.id]);
          if (lastAnsweredIndex >= 0) {
            // Show the last answered question so user can see their previous answer
            setCurrentQuestionIndex(lastAnsweredIndex);
          }
        }

      } catch (err) {
        console.error('Failed to load test data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load test data');
      } finally {
        setLoading(false);
      }
    };

    loadTestData();
  }, [testId, currentUser, flattenQuestions, initializeQuestionStatuses]);

  // Auto-save progress
  const saveProgress = useCallback(async (updatedAttempt: TestAttempt) => {
    if (!updatedAttempt) return;

    setSaving(true);
    try {
      const savedAttempt = await progressStore.saveProgress({
        ...updatedAttempt,
        timeRemaining
      });
      // Update the current attempt with any changes (like ID assignment)
      setCurrentAttempt(savedAttempt);
    } catch (err) {
      console.error('Failed to save progress:', err);
    } finally {
      setSaving(false);
    }
  }, [timeRemaining]);

  // Debounced save
  const debouncedSave = useCallback((attempt: TestAttempt) => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    saveTimeoutRef.current = setTimeout(() => {
      saveProgress(attempt);
    }, 2000);
  }, [saveProgress]);

  // Auto-submit when time expires
  const handleAutoSubmit = useCallback(async () => {
    if (!currentAttempt || !testData) return;

    try {
      // Calculate final scores
      const scores = scoringEngine.calculateTestScores(testData, currentAttempt);
      
      const finalAttempt = {
        ...currentAttempt,
        endTime: new Date(),
        timeRemaining: 0,
        isCompleted: true,
        scores
      };

      // Submit final results
      await progressStore.submitFinalResults(finalAttempt);
      onNavigate('test-summary', testId);
    } catch (err) {
      console.error('Failed to auto-submit test:', err);
    }
  }, [currentAttempt, testData, onNavigate, testId]);

  // Timer effect
  useEffect(() => {
    if (timeRemaining <= 0 || !currentAttempt || currentAttempt.isCompleted) {
      return;
    }

    timerRef.current = setInterval(() => {
      setTimeRemaining(prev => {
        const newTime = prev - 1;
        if (newTime <= 0) {
          handleAutoSubmit();
          return 0;
        }
        return newTime;
      });
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [timeRemaining, currentAttempt, handleAutoSubmit]);

  // Handle answer selection
  const handleAnswerSelect = useCallback((questionId: string, choice: ChoiceId) => {
    if (!currentAttempt) return;

    // Check if clicking the same answer - if so, unselect it
    const currentAnswer = currentAttempt.answers[questionId];
    const isUnselectingSameAnswer = currentAnswer === choice;

    const updatedAnswers = { ...currentAttempt.answers };
    
    if (isUnselectingSameAnswer) {
      // Remove the answer
      delete updatedAnswers[questionId];
    } else {
      // Set new answer
      updatedAnswers[questionId] = choice;
    }

    const updatedAttempt = {
      ...currentAttempt,
      answers: updatedAnswers
    };

    setCurrentAttempt(updatedAttempt);
    
    // Update question status
    setQuestionStatuses(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        answered: !isUnselectingSameAnswer
      }
    }));

    debouncedSave(updatedAttempt);
  }, [currentAttempt, debouncedSave]);

  // Handle question flagging
  const handleToggleFlag = useCallback((questionId: string) => {
    if (!currentAttempt) return;

    const newFlagged = new Set(currentAttempt.flagged);
    if (newFlagged.has(questionId)) {
      newFlagged.delete(questionId);
    } else {
      newFlagged.add(questionId);
    }

    const updatedAttempt = {
      ...currentAttempt,
      flagged: newFlagged
    };

    setCurrentAttempt(updatedAttempt);
    
    // Update question status
    setQuestionStatuses(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        flagged: newFlagged.has(questionId)
      }
    }));

    debouncedSave(updatedAttempt);
  }, [currentAttempt, debouncedSave]);

  // Navigation functions
  const goToQuestion = useCallback((index: number) => {
    if (index >= 0 && index < allQuestions.length) {
      setCurrentQuestionIndex(index);
      setShowNavigationGrid(false);
    }
  }, [allQuestions.length]);

  const goToPrevious = useCallback(() => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  }, [currentQuestionIndex]);

  const goToNext = useCallback(() => {
    if (currentQuestionIndex < allQuestions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  }, [currentQuestionIndex, allQuestions.length]);

  // Keyboard navigation handler
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    // Don't interfere with form inputs or modals
    if (event.target instanceof HTMLInputElement || 
        event.target instanceof HTMLTextAreaElement ||
        showSubmitConfirmation) {
      return;
    }

    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        goToPrevious();
        break;
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        goToNext();
        break;
      case '1':
      case '2':
      case '3':
      case '4':
        if (!event.ctrlKey && !event.altKey && !event.metaKey) {
          event.preventDefault();
          const choiceIndex = parseInt(event.key) - 1;
          const currentQuestion = allQuestions[currentQuestionIndex];
          if (currentQuestion && currentQuestion.choices[choiceIndex]) {
            handleAnswerSelect(currentQuestion.id, currentQuestion.choices[choiceIndex].id);
          }
        }
        break;
      case 'a':
      case 'A':
        if (!event.ctrlKey && !event.altKey && !event.metaKey) {
          event.preventDefault();
          const currentQuestion = allQuestions[currentQuestionIndex];
          if (currentQuestion) {
            handleAnswerSelect(currentQuestion.id, 'A');
          }
        }
        break;
      case 'b':
      case 'B':
        if (!event.ctrlKey && !event.altKey && !event.metaKey) {
          event.preventDefault();
          const currentQuestion = allQuestions[currentQuestionIndex];
          if (currentQuestion) {
            handleAnswerSelect(currentQuestion.id, 'B');
          }
        }
        break;
      case 'c':
      case 'C':
        if (!event.ctrlKey && !event.altKey && !event.metaKey) {
          event.preventDefault();
          const currentQuestion = allQuestions[currentQuestionIndex];
          if (currentQuestion) {
            handleAnswerSelect(currentQuestion.id, 'C');
          }
        }
        break;
      case 'd':
      case 'D':
        if (!event.ctrlKey && !event.altKey && !event.metaKey) {
          event.preventDefault();
          const currentQuestion = allQuestions[currentQuestionIndex];
          if (currentQuestion) {
            handleAnswerSelect(currentQuestion.id, 'D');
          }
        }
        break;
      case 'f':
      case 'F':
        if (!event.ctrlKey && !event.altKey && !event.metaKey) {
          event.preventDefault();
          const currentQuestion = allQuestions[currentQuestionIndex];
          if (currentQuestion) {
            handleToggleFlag(currentQuestion.id);
          }
        }
        break;
      case 'k':
      case 'K':
        if (!event.ctrlKey && !event.altKey && !event.metaKey) {
          event.preventDefault();
          setShowKeyboardHelp(!showKeyboardHelp);
        }
        break;
      case 'Escape':
        event.preventDefault();
        if (showSubmitConfirmation) {
          setShowSubmitConfirmation(false);
        } else if (showKeyboardHelp) {
          setShowKeyboardHelp(false);
        }
        break;
    }
  }, [currentQuestionIndex, allQuestions, goToPrevious, goToNext, handleAnswerSelect, handleToggleFlag, showSubmitConfirmation, showKeyboardHelp]);

  // Add keyboard event listener
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  // Handle manual submit
  const handleSubmit = useCallback(async () => {
    if (!currentAttempt || !testData) {
      return;
    }

    if (!currentAttempt.id) {
      console.error('Current attempt has no ID, cannot submit');
      alert('Chyba: Test nemá přiřazené ID. Zkuste obnovit stránku a začít znovu.');
      return;
    }

    if (submitting) {
      return;
    }

    try {
      setSubmitting(true);
      
      // Calculate final scores
      const scores = scoringEngine.calculateTestScores(testData, currentAttempt);
      
      const finalAttempt = {
        ...currentAttempt,
        endTime: new Date(),
        timeRemaining,
        isCompleted: true,
        scores
      };

      // Submit final results
      await progressStore.submitFinalResults(finalAttempt);
      
      setShowSubmitConfirmation(false);
      onNavigate('test-summary', testId);
    } catch (err) {
      console.error('Failed to submit test:', err);
      // Show user-friendly error message
      alert('Chyba při odevzdávání testu: ' + (err instanceof Error ? err.message : 'Neznámá chyba'));
    } finally {
      setSubmitting(false);
    }
  }, [currentAttempt, testData, timeRemaining, onNavigate, testId, submitting]);

  // Format time display
  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  // Get question status counts
  const getStatusCounts = () => {
    const answered = Object.values(questionStatuses).filter(status => status.answered).length;
    const flagged = Object.values(questionStatuses).filter(status => status.flagged).length;
    const unanswered = allQuestions.length - answered;
    
    return { answered, flagged, unanswered };
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-6xl mb-4">⚠️</div>
        <h2 className="text-2xl font-semibold text-red-600 dark:text-red-400 mb-2 font-poppins">
          Chyba při načítání testu
        </h2>
        <p className="text-light-text-secondary dark:text-dark-text-secondary font-poppins mb-4">
          {error}
        </p>
        <button
          onClick={() => onNavigate('test-list')}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded-lg font-poppins font-medium transition-colors duration-200"
        >
          Zpět na seznam testů
        </button>
      </div>
    );
  }

  if (!testData || !currentAttempt || allQuestions.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-6xl mb-4">❓</div>
        <h2 className="text-2xl font-semibold text-light-text-primary dark:text-dark-text-primary mb-2 font-poppins">
          Test nenalezen
        </h2>
        <p className="text-light-text-secondary dark:text-dark-text-secondary font-poppins mb-4">
          Požadovaný test nebyl nalezen nebo není dostupný.
        </p>
        <button
          onClick={() => onNavigate('test-list')}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded-lg font-poppins font-medium transition-colors duration-200"
        >
          Zpět na seznam testů
        </button>
      </div>
    );
  }

  const currentQuestion = allQuestions[currentQuestionIndex];
  const statusCounts = getStatusCounts();
  const isTimeRunningOut = timeRemaining <= 300; // 5 minutes

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Skip Link */}
      <a 
        href="#main-question"
        className="skip-link sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 bg-blue-600 text-white px-4 py-2 rounded-lg z-50"
      >
        Přejít na otázku
      </a>
      
      {/* Header with timer and progress */}
      <header 
        className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-10"
        role="banner"
        aria-label="Test header"
      >
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            {/* Test info */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4">
              <h1 className="text-lg sm:text-xl font-semibold text-light-text-primary dark:text-dark-text-primary font-poppins">
                {testData.title}
              </h1>
              <div className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                Otázka {currentQuestionIndex + 1} z {allQuestions.length}
              </div>
            </div>

            {/* Timer and status */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6 w-full sm:w-auto">
              {/* Progress indicators - mobile optimized */}
              <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm">
                <div className="flex items-center">
                  <div 
                    className="w-3 h-3 bg-green-500 rounded-full mr-2"
                    aria-hidden="true"
                  ></div>
                  <span className="text-light-text-secondary dark:text-dark-text-secondary">
                    <span className="sr-only">Zodpovězených otázek: </span>
                    <span className="hidden sm:inline">Zodpovězeno: </span>
                    {statusCounts.answered}
                  </span>
                </div>
                <div className="flex items-center">
                  <div 
                    className="w-3 h-3 bg-yellow-500 rounded-full mr-2"
                    aria-hidden="true"
                  ></div>
                  <span className="text-light-text-secondary dark:text-dark-text-secondary">
                    <span className="sr-only">Označených otázek: </span>
                    <span className="hidden sm:inline">Označeno: </span>
                    {statusCounts.flagged}
                  </span>
                </div>
                <div className="flex items-center">
                  <div 
                    className="w-3 h-3 bg-gray-400 rounded-full mr-2"
                    aria-hidden="true"
                  ></div>
                  <span className="text-light-text-secondary dark:text-dark-text-secondary">
                    <span className="sr-only">Nezodpovězených otázek: </span>
                    <span className="hidden sm:inline">Nezodpovězeno: </span>
                    {statusCounts.unanswered}
                  </span>
                </div>
              </div>

              {/* Save indicator */}
              {saving && (
                <div 
                  className="flex items-center text-sm text-blue-600 dark:text-blue-400"
                  role="status"
                  aria-live="polite"
                >
                  <svg 
                    className="animate-spin w-4 h-4 mr-2" 
                    fill="none" 
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Ukládání...</span>
                </div>
              )}

              {/* Timer */}
              <div 
                className={`text-lg font-mono font-bold px-3 py-2 rounded-lg ${
                  isTimeRunningOut 
                    ? 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800' 
                    : 'text-light-text-primary dark:text-dark-text-primary bg-gray-100 dark:bg-gray-700'
                }`}
                role="timer"
                aria-live="polite"
                aria-label={`Zbývající čas: ${formatTime(timeRemaining)}`}
              >
                {formatTime(timeRemaining)}
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto p-4">
        <div className="grid lg:grid-cols-4 gap-6">
          {/* Main question area */}
          <main className="lg:col-span-3" role="main" aria-label="Test question">
            <div id="main-question" className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
              {/* Question header */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-3">
                  <span className="text-sm font-medium text-light-text-secondary dark:text-dark-text-secondary">
                    Otázka {currentQuestionIndex + 1}
                  </span>
                  {questionStatuses[currentQuestion.id]?.flagged && (
                    <div 
                      className="flex items-center text-yellow-600 dark:text-yellow-400 mt-1 sm:mt-0"
                      role="status"
                      aria-label="Otázka je označena"
                    >
                      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                        <path fillRule="evenodd" d="M3 6a3 3 0 013-3h10a1 1 0 01.8 1.6L14.25 8l2.55 3.4A1 1 0 0116 13H6a1 1 0 00-1 1v3a1 1 0 11-2 0V6z" clipRule="evenodd" />
                      </svg>
                      <span className="text-sm">Označeno</span>
                    </div>
                  )}
                </div>
                
                <button
                  onClick={() => handleToggleFlag(currentQuestion.id)}
                  className={`p-2 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 dark:focus:ring-offset-gray-800 ${
                    questionStatuses[currentQuestion.id]?.flagged
                      ? 'text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/20 hover:bg-yellow-100 dark:hover:bg-yellow-900/30'
                      : 'text-gray-400 dark:text-gray-500 hover:text-yellow-600 dark:hover:text-yellow-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                  aria-label={questionStatuses[currentQuestion.id]?.flagged ? 'Odebrat označení otázky' : 'Označit otázku pro pozdější kontrolu'}
                  aria-pressed={questionStatuses[currentQuestion.id]?.flagged}
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                    <path fillRule="evenodd" d="M3 6a3 3 0 013-3h10a1 1 0 01.8 1.6L14.25 8l2.55 3.4A1 1 0 0116 13H6a1 1 0 00-1 1v3a1 1 0 11-2 0V6z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>

              {/* Question stem */}
              <div className="mb-6">
                <div 
                  className="text-base sm:text-lg text-light-text-primary dark:text-dark-text-primary leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: currentQuestion.stemMd }}
                  role="heading"
                  aria-level={2}
                />
              </div>

              {/* Answer choices */}
              <fieldset className="space-y-3">
                <legend className="sr-only">
                  Vyberte odpověď na otázku {currentQuestionIndex + 1}
                </legend>
                {currentQuestion.choices.map((choice, index) => (
                  <label
                    key={choice.id}
                    className={`flex items-start p-3 sm:p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2 dark:focus-within:ring-offset-gray-800 ${
                      currentAttempt.answers[currentQuestion.id] === choice.id
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                    onClick={(e) => {
                      e.preventDefault();
                      handleAnswerSelect(currentQuestion.id, choice.id);
                    }}
                  >
                    <input
                      type="radio"
                      name={`question-${currentQuestion.id}`}
                      value={choice.id}
                      checked={currentAttempt.answers[currentQuestion.id] === choice.id}
                      onChange={() => {}}
                      className="mt-1 mr-3 sm:mr-4 text-blue-600 focus:ring-blue-500 w-3 h-3 sm:w-4 sm:h-4 pointer-events-none"
                      aria-describedby={`choice-${choice.id}-text`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start">
                        <span className="font-semibold text-light-text-primary dark:text-dark-text-primary mr-3 text-sm sm:text-base flex-shrink-0">
                          {choice.id}.
                        </span>
                        <div 
                          id={`choice-${choice.id}-text`}
                          className="text-light-text-primary dark:text-dark-text-primary text-sm sm:text-base break-words"
                          dangerouslySetInnerHTML={{ __html: choice.textMd }}
                        />
                      </div>
                    </div>
                  </label>
                ))}
              </fieldset>

              {/* Mobile navigation buttons - shown only on mobile */}
              <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200 dark:border-gray-600 sm:hidden">
                <button
                  onClick={goToPrevious}
                  disabled={currentQuestionIndex === 0}
                  className="flex items-center justify-center px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 rounded-lg bg-gray-100 dark:bg-gray-700"
                  aria-label="Přejít na předchozí otázku"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  <span className="text-sm font-medium">Předchozí</span>
                </button>

                <div className="text-sm text-gray-500 dark:text-gray-400 font-medium">
                  {currentQuestionIndex + 1} / {allQuestions.length}
                </div>

                <button
                  onClick={goToNext}
                  disabled={currentQuestionIndex === allQuestions.length - 1}
                  className="flex items-center justify-center px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 rounded-lg bg-gray-100 dark:bg-gray-700"
                  aria-label="Přejít na další otázku"
                >
                  <span className="text-sm font-medium">Další</span>
                  <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>

              {/* Mobile submit button - shown only on mobile */}
              <div className="mt-4 sm:hidden">
                <button
                  onClick={() => setShowSubmitConfirmation(true)}
                  className="w-full px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
                  aria-label="Odevzdat test - otevře potvrzovací dialog"
                >
                  Odevzdat test
                </button>
              </div>

              {/* Navigation buttons - hidden on mobile */}
              <nav 
                className="hidden sm:flex flex-col sm:flex-row items-stretch sm:items-center justify-between mt-8 pt-6 border-t border-gray-200 dark:border-gray-600 gap-4"
                role="navigation"
                aria-label="Question navigation"
              >
                <button
                  onClick={goToPrevious}
                  disabled={currentQuestionIndex === 0}
                  className="flex items-center justify-center px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 rounded-lg"
                  aria-label="Přejít na předchozí otázku"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  <span className="hidden sm:inline">Předchozí</span>
                  <span className="sm:hidden">Předchozí otázka</span>
                </button>

                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
                  <button
                    onClick={() => setShowNavigationGrid(!showNavigationGrid)}
                    className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 lg:hidden"
                    aria-label="Zobrazit přehled všech otázek"
                    aria-expanded={showNavigationGrid}
                  >
                    Přehled otázek
                  </button>
                  
                  <button
                    onClick={() => setShowKeyboardHelp(!showKeyboardHelp)}
                    className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
                    aria-label="Zobrazit klávesové zkratky"
                    title="Klávesové zkratky (K)"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l4-4 4 4m0 6l-4 4-4-4" />
                    </svg>
                  </button>
                  
                  <button
                    onClick={() => setShowSubmitConfirmation(true)}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
                    aria-label="Odevzdat test - otevře potvrzovací dialog"
                  >
                    Odevzdat test
                  </button>
                </div>

                <button
                  onClick={goToNext}
                  disabled={currentQuestionIndex === allQuestions.length - 1}
                  className="flex items-center justify-center px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 rounded-lg"
                  aria-label="Přejít na další otázku"
                >
                  <span className="hidden sm:inline">Další</span>
                  <span className="sm:hidden">Další otázka</span>
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </nav>
            </div>
          </main>

          {/* Navigation grid sidebar */}
          <aside className="lg:col-span-1" aria-label="Question navigation">
            <NavigationGrid
              questions={allQuestions}
              questionStatuses={questionStatuses}
              currentQuestionIndex={currentQuestionIndex}
              onQuestionSelect={goToQuestion}
            />
          </aside>
        </div>
      </div>

      {/* Keyboard Help Modal */}
      {showKeyboardHelp && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowKeyboardHelp(false);
            }
          }}
          role="dialog"
          aria-labelledby="keyboard-help-title"
          aria-modal="true"
        >
          <div 
            className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 id="keyboard-help-title" className="text-lg font-semibold text-light-text-primary dark:text-dark-text-primary">
                Klávesové zkratky
              </h3>
              <button
                onClick={() => setShowKeyboardHelp(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-lg p-1"
                aria-label="Zavřít nápovědu"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-light-text-primary dark:text-dark-text-primary mb-2">Navigace</h4>
                  <div className="space-y-1 text-light-text-secondary dark:text-dark-text-secondary">
                    <div className="flex justify-between">
                      <span>Předchozí otázka:</span>
                      <kbd className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-xs">←</kbd>
                    </div>
                    <div className="flex justify-between">
                      <span>Další otázka:</span>
                      <kbd className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-xs">→</kbd>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h4 className="font-medium text-light-text-primary dark:text-dark-text-primary mb-2">Odpovědi</h4>
                  <div className="space-y-1 text-light-text-secondary dark:text-dark-text-secondary">
                    <div className="flex justify-between">
                      <span>Odpověď A:</span>
                      <kbd className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-xs">A</kbd>
                    </div>
                    <div className="flex justify-between">
                      <span>Odpověď B:</span>
                      <kbd className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-xs">B</kbd>
                    </div>
                    <div className="flex justify-between">
                      <span>Odpověď C:</span>
                      <kbd className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-xs">C</kbd>
                    </div>
                    <div className="flex justify-between">
                      <span>Odpověď D:</span>
                      <kbd className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-xs">D</kbd>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="pt-3 border-t border-gray-200 dark:border-gray-600">
                <h4 className="font-medium text-light-text-primary dark:text-dark-text-primary mb-2">Ostatní</h4>
                <div className="space-y-1 text-light-text-secondary dark:text-dark-text-secondary">
                  <div className="flex justify-between">
                    <span>Označit otázku:</span>
                    <kbd className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-xs">F</kbd>
                  </div>
                  <div className="flex justify-between">
                    <span>Tato nápověda:</span>
                    <kbd className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-xs">K</kbd>
                  </div>
                  <div className="flex justify-between">
                    <span>Zavřít dialog:</span>
                    <kbd className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-xs">Esc</kbd>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowKeyboardHelp(false)}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 focus:bg-blue-700 text-white rounded-lg font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
              >
                Rozumím
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Navigation Grid Overlay */}
      {showNavigationGrid && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 lg:hidden"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowNavigationGrid(false);
            }
          }}
          role="dialog"
          aria-labelledby="mobile-navigation-title"
          aria-modal="true"
        >
          <div 
            className="bg-white dark:bg-gray-800 rounded-lg max-w-lg w-full max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-4 rounded-t-lg">
              <div className="flex items-center justify-between">
                <h3 id="mobile-navigation-title" className="text-lg font-semibold text-light-text-primary dark:text-dark-text-primary">
                  Navigace otázek
                </h3>
                <button
                  onClick={() => setShowNavigationGrid(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-lg p-1"
                  aria-label="Zavřít navigaci"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="p-6">
              <NavigationGrid
                questions={allQuestions}
                questionStatuses={questionStatuses}
                currentQuestionIndex={currentQuestionIndex}
                onQuestionSelect={(index) => {
                  goToQuestion(index);
                  setShowNavigationGrid(false);
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Submit confirmation modal */}
      {showSubmitConfirmation && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            // Close modal if clicking on backdrop
            if (e.target === e.currentTarget) {
              setShowSubmitConfirmation(false);
            }
          }}
        >
          <div 
            className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center mb-4">
              <svg className="w-6 h-6 text-red-600 dark:text-red-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <h3 className="text-lg font-semibold text-light-text-primary dark:text-dark-text-primary">
                Odevzdat test
              </h3>
            </div>
            
            <p className="text-light-text-secondary dark:text-dark-text-secondary mb-4">
              Opravdu chcete odevzdat test? Po odevzdání již nebudete moci měnit své odpovědi.
            </p>
            
            <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <h4 className="font-medium text-light-text-primary dark:text-dark-text-primary mb-3">
                Přehled odpovědí
              </h4>
              <div className="text-sm space-y-2">
                <div className="flex justify-between">
                  <span>Zodpovězeno otázek:</span>
                  <span className="font-medium text-green-600 dark:text-green-400">
                    {statusCounts.answered} z {allQuestions.length}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Označeno k přezkoumání:</span>
                  <span className="font-medium text-yellow-600 dark:text-yellow-400">
                    {statusCounts.flagged}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Nezodpovězeno:</span>
                  <span className="font-medium text-red-600 dark:text-red-400">
                    {statusCounts.unanswered}
                  </span>
                </div>
                <div className="flex justify-between border-t border-gray-200 dark:border-gray-600 pt-2 mt-2">
                  <span>Zbývající čas:</span>
                  <span className={`font-medium ${isTimeRunningOut ? 'text-red-600 dark:text-red-400' : 'text-light-text-primary dark:text-dark-text-primary'}`}>
                    {formatTime(timeRemaining)}
                  </span>
                </div>
              </div>
            </div>
            
            {statusCounts.unanswered > 0 && (
              <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm text-yellow-800 dark:text-yellow-200">
                    Máte {statusCounts.unanswered} nezodpovězených otázek
                  </span>
                </div>
              </div>
            )}
            
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => setShowSubmitConfirmation(false)}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 rounded-lg font-medium hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors duration-200"
              >
                Pokračovat v testu
              </button>
              {statusCounts.flagged > 0 && (
                <button
                  onClick={() => {
                    setShowSubmitConfirmation(false);
                    // Navigate to first flagged question
                    const firstFlaggedIndex = allQuestions.findIndex(q => questionStatuses[q.id]?.flagged);
                    if (firstFlaggedIndex >= 0) {
                      setCurrentQuestionIndex(firstFlaggedIndex);
                    }
                  }}
                  className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg font-medium transition-colors duration-200"
                >
                  Zkontrolovat označené
                </button>
              )}
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className={`px-4 py-2 text-white rounded-lg font-medium transition-colors duration-200 flex items-center ${
                  submitting 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {submitting && (
                  <svg className="animate-spin w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                {submitting ? 'Odevzdávání...' : 'Definitivně odevzdat'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestRunner;